import { BacklogsHubConstants } from "Agile/Scripts/Generated/HubConstants";
import * as React from "react";
import { registerLWPComponent } from "VSS/LWP";
import { VssComponent } from "VSS/Platform/Layout";
import { publishEvent, TelemetryEventData } from "VSS/Telemetry/Services";
import { Uri } from "VSS/Utils/Url";
import { LWPComponent } from "VSSPreview/Flux/Components/LWP";

/**
 * Additional properties to be published via telemetry
 */
interface ITelemetryProps{
    parentPageName?: string;
    area?: string
}

/**
 * Describes context required for reports shared by Backlog and Boards area.
 */
export interface BacklogsAnalyticsViewProps {
    teamId: string;
    teamName: string;
    projectId: string;
    /** The invariant category reference Name used to reliably identify a backlog */
    categoryReferenceName: string;
    /** The user facing name of backlog, used for routing  */
    categoryName: string;
    lastRefresh?: Date;
    onLoadCompleted: () => void;
    telemetryProps?: ITelemetryProps
}

export interface BacklogsAnalyticsViewState {
    /** This is a specialization for these views to allow for navigation between reports without the use of distinct pivots, required for design alignment with Pipelines reports. */
    activeReport?: string;
}

/**
 * This Component mediates the sub-pivot level navigation for analytics reports on the boards and backlogs pages.
 */
export class BacklogsAnalyticsView extends VssComponent<BacklogsAnalyticsViewProps, BacklogsAnalyticsViewState> {
    public static componentType = "backlogsAnalyticsView";
    constructor(props: BacklogsAnalyticsViewProps, context?: any) {
        super(props, context);
        this.state = {
            activeReport: this.getActiveReport()
        };
    }

    public componentWillUpdate(prevProps: Readonly<BacklogsAnalyticsViewProps>, prevState: Readonly<BacklogsAnalyticsViewState>, snapshot?: {}) {
        const activeReport = this.getActiveReport();
        if (this.state.activeReport !== activeReport) {
            this.setState({ activeReport });
        }

        // publish when user selects work item type from dropdown
        if(this.props.categoryName !== prevProps.categoryName){
            this.publishActiveReportelemetry("DropDownClick-WorkItemType")
        }
    }

    public render(): JSX.Element {
        let content = null;
        switch (this.state.activeReport) {
            case BacklogsHubConstants.VelocityReportName:
                content = renderVelocityReport(this.props);
                break;
            case BacklogsHubConstants.CFDReportName:
                content = renderCFDReport(this.props);
                break;
            default:
                content = renderReportSelector(this.props);
                break;
        }
        return content;
    }

    private publishActiveReportelemetry(scenario: string){
        const eventData: TelemetryEventData = {
            area: this.props.telemetryProps?.area,
            feature: "AnalyticsTab",
            properties: {
                Scenario: scenario,
                TeamId: this.props.teamId,
                ParentPageName: this.props.telemetryProps?.parentPageName
            },
            elapsedTime: 0
        }

        publishEvent(eventData);
    }
    /**
     * Extracts information about the selected report from the Url. This can be undefined.
     * This path is resposible for support navigation of reports from inside the backlogs/boards hubs.
     */
    private getActiveReport(): string | undefined {
        // Note: Legacy stack route information from location service does not hold up with reality after legacy page pivot SPA state changes.
        // In lieu of routeParams data, mitigation is a check for recognized report location at end of known path structure

        const routeParams = new Uri(window.location.href).path.split("/");
        const reportCandidate = routeParams[routeParams.length - 1].toLowerCase();
        return reportCandidate === BacklogsHubConstants.VelocityReportName || reportCandidate === BacklogsHubConstants.CFDReportName
            ? reportCandidate
            : undefined;
    }
}

registerLWPComponent(BacklogsAnalyticsView.componentType, BacklogsAnalyticsView);

function renderReportSelector(reportSettings): JSX.Element {
    return (
        <LWPComponent wrappedType={"BoardsReportSelector"} dependencies={["ms.vss-reporting-web.boards-report-view-content"]} {...reportSettings} />
    );
}

function renderVelocityReport(reportSettings): JSX.Element {
    return <LWPComponent wrappedType={"VelocityReport"} dependencies={["ms.vss-reporting-web.velocity-report-content"]} {...reportSettings} />;
}

function renderCFDReport(reportSettings): JSX.Element {
    return <LWPComponent wrappedType={"CFDReport"} dependencies={["ms.vss-reporting-web.cfd-report-content"]} {...reportSettings} />;
}
